// @flow
import * as React from 'react';
import { getImageUrl, getItemUrl } from '../../utils/urls';
import CardStatusBanner from './CardStatusBanner';
import classNames from 'classnames';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback';
import ImageWithHover from '../ImageWithHover/ImageWithHover';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styles from './ImageRow.module.scss';

type Props = {
    accountInGoodStanding: boolean,
    bidderHasBid: boolean,
    bidderHasHighBid: boolean,
    blockedByAuctioneer: boolean,
    catalogId: number,
    imageVersion: number,
    isAvailable: boolean,
    isPassed: boolean,
    isReserveMet: boolean,
    isSEOExcluded: boolean,
    itemId: number,
    noWidthLimit: boolean,
    openOnBlank: boolean,
    pending: boolean,
    photo?: number,
    photos?: number[],
    sellerId: number,
    showBidStatus: boolean,
    showImageWithHover: boolean,
    skipVisibilityCheck: boolean,
    suspended: boolean,
    title: string,
};

const ImageRow = (props: Props) => {
    const {
        accountInGoodStanding,
        bidderHasBid,
        bidderHasHighBid,
        blockedByAuctioneer,
        isAvailable,
        isPassed,
        isSEOExcluded,
        isReserveMet,
        noWidthLimit,
        openOnBlank,
        pending,
        showBidStatus,
        showImageWithHover = false,
        skipVisibilityCheck,
        suspended,
        catalogId,
        imageVersion,
        itemId,
        photo,
        photos,
        sellerId,
        title,
    } = props;

    let firstPhoto;
    if (photo) {
        // came from the catalog page items
        firstPhoto = photo;
    } else if (photos && photos.length > 0) {
        firstPhoto = photos[0];
    } else {
        firstPhoto = 0;
    }

    const itemUrl = getItemUrl(itemId, title);

    const imageOptions: any = {
        catalogId,
        imageNumber: firstPhoto,
        imageVersion,
        itemId,
        sellerId,
    };

    if (noWidthLimit) {
        imageOptions.height = 190;
    } else {
        imageOptions.width = 310;
    }

    const imageUrl = getImageUrl(imageOptions);

    // get 2nd photo info
    let secondaryImageUrl, secondPhoto;
    if (photos && photos.length > 1) {
        secondPhoto = firstPhoto === photos[0] ? photos[1] : photos[0];
    }

    if (Boolean(secondPhoto)) {
        const secondImageOptions = Object.assign({}, imageOptions);
        secondImageOptions.imageNumber = secondPhoto;
        secondaryImageUrl = getImageUrl(secondImageOptions);
    }
    const targetProps = openOnBlank
        ? { rel: isSEOExcluded ? 'nofollow' : '', route: false, target: '_blank' }
        : { rel: isSEOExcluded ? 'nofollow' : '', route: true };

    const classes = classNames(styles['image-container'], { [styles.noLimit]: noWidthLimit });

    // image-with-hover and image-without-hover exist for A/B testing
    return (
        <Link className={styles['item-image-link-container']} {...targetProps} to={itemUrl}>
            <div className={classes}>
                <div className={styles['image-with-hover']}>
                    {Boolean(showImageWithHover) && Boolean(secondaryImageUrl) ? (
                        <ImageWithHover
                            alt={title}
                            className={styles.image}
                            forceFallback={!imageUrl}
                            srcPrimary={imageUrl}
                            srcSecondary={secondaryImageUrl}
                            skipVisibilityCheck={skipVisibilityCheck}
                        />
                    ) : (
                        <ImageWithFallback
                            alt={title}
                            className={styles.image}
                            forceFallback={!imageUrl}
                            src={imageUrl}
                            skipVisibilityCheck={skipVisibilityCheck}
                        />
                    )}
                </div>
                {showBidStatus && (
                    <CardStatusBanner
                        accountInGoodStanding={accountInGoodStanding}
                        bidderHasBid={bidderHasBid}
                        bidderHasHighBid={bidderHasHighBid}
                        blockedByAuctioneer={blockedByAuctioneer}
                        isAvailable={isAvailable}
                        isPassed={isPassed}
                        isReserveMet={isReserveMet}
                        pending={pending}
                        suspended={suspended}
                    />
                )}
            </div>
        </Link>
    );
};

export default React.memo<Props>(ImageRow, isEqual);
